import React, { Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChartFallbackPanel } from "./ChartFallbackPanel";
import { useArtifactSourceRegistry } from "../../pages/workspace/panels/artifactProvenance";
import { SourceColChip } from "./SourceColChip";

// Convert an rgb()/rgba()/#hex color to an rgba() string at the given alpha.
// Used to derive faint grid/axis lines from the surface's text color so they
// stay visible on any background. Returns the input unchanged if unparseable.
export function rgbaFrom(colorStr, alpha) {
  if (!colorStr || typeof colorStr !== "string") return colorStr;
  const s = colorStr.trim();
  let r, g, b;
  if (s[0] === "#") {
    const h = s.slice(1);
    const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    if (n.length < 6) return colorStr;
    r = parseInt(n.slice(0, 2), 16); g = parseInt(n.slice(2, 4), 16); b = parseInt(n.slice(4, 6), 16);
  } else {
    const m = s.match(/(\d+(?:\.\d+)?)/g);
    if (!m || m.length < 3) return colorStr;
    [r, g, b] = m.map(Number);
  }
  return `rgba(${r},${g},${b},${alpha})`;
}

// Build the Plotly layout, making text/axis colors adapt to the surface the
// chart is rendered on. The backend emits a bare layout with no theming, so if
// we hardcode a light font (as we used to) it's invisible on the light V2 cards
// while looking fine on the dark canvas. `textColor` is the surface's resolved
// CSS text color; font/grid/line colors derive from it. Backend-supplied keys
// win over our derived defaults, and font size/family are preserved.
export function buildPlotlyLayout(specLayout, textColor) {
  const base = specLayout || {};
  const color = textColor || "#e2e8f0";
  const axisDefaults = {
    gridcolor: rgbaFrom(color, 0.15),
    zerolinecolor: rgbaFrom(color, 0.45),
    linecolor: rgbaFrom(color, 0.45),
    tickcolor: rgbaFrom(color, 0.45),
  };
  const layout = {
    ...base,
    autosize: true,
    paper_bgcolor: "transparent",
    plot_bgcolor: "transparent",
    font: { ...(base.font || {}), color },
    xaxis: { ...axisDefaults, ...(base.xaxis || {}) },
    yaxis: { ...axisDefaults, ...(base.yaxis || {}) },
  };
  if (base.xaxis2) layout.xaxis2 = { ...axisDefaults, ...base.xaxis2 };
  if (base.yaxis2) layout.yaxis2 = { ...axisDefaults, ...base.yaxis2 };
  return layout;
}

// Lazy-load the Plotly bundle so it only ships when the canvas opens.
// We use the factory pattern with plotly.js-dist-min (the modular bundle) instead
// of the default react-plotly.js entry, which would require the full plotly.js
// package (~3.5MB raw vs ~1MB modular).
const Plot = React.lazy(async () => {
  const [{ default: createPlotlyComponent }, { default: Plotly }] = await Promise.all([
    import("react-plotly.js/factory"),
    import("plotly.js-dist-min"),
  ]);
  // Expose for ResizeObserver to call resize on container changes (canvas drag, rotation, etc.)
  if (typeof window !== 'undefined') {
    window.Plotly = window.Plotly || Plotly;
  }
  return { default: createPlotlyComponent(Plotly) };
});

class PlotlyErrorBoundary extends React.Component {
  state = { error: null, lastSpec: undefined };
  static getDerivedStateFromError(error) { return { error }; }
  // Clear the error when a NEW spec arrives. Without this the boundary latches:
  // once a spec throws, onRegenerate's fallback button is permanently dead for
  // that instance, because regenerating reuses the same chart_id and so does not
  // change component identity (PLEX-4227 review). Keyed on spec identity — the
  // regenerate path produces a fresh spec object — so a re-render with the same
  // spec keeps the error rather than flickering back to a throwing child.
  static getDerivedStateFromProps(props, state) {
    if (state.lastSpec !== props.resetSpec) {
      return { error: null, lastSpec: props.resetSpec };
    }
    return null;
  }
  componentDidCatch(err, info) { console.error("[PlotlyHost] render error", err, info); }
  render() {
    if (this.state.error) {
      return <ChartFallbackPanel title={this.props.title} error={this.state.error.message} onRegenerate={this.props.onRegenerate} />;
    }
    return this.props.children;
  }
}

export const PlotlyHost = ({ spec, title, onRegenerate, sourceReferencesStore }) => {
  const containerRef = useRef(null);
  // Resolve the surface's text color so chart text/axes contrast against
  // whatever card the chart is mounted in (dark canvas vs light V2 card).
  // Read before paint via useLayoutEffect to avoid a flash of the fallback.
  const [surfaceColor, setSurfaceColor] = useState(null);
  useLayoutEffect(() => {
    if (containerRef.current && typeof getComputedStyle === "function") {
      const c = getComputedStyle(containerRef.current).color;
      if (c) setSurfaceColor(c);
    }
  }, []);

  const { bySpecId, columnSourceId, storeIdFor, resolveStoreId } = useArtifactSourceRegistry(
    spec,
    sourceReferencesStore,
    { turnId: `plotly-${spec?.chart_id || spec?.id || "anon"}` },
  );

  // Trigger Plotly to recalc size when our container resizes (canvas drag, mobile rotation, etc.)
  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const plotEl = containerRef.current?.querySelector('.js-plotly-plot');
      if (plotEl && typeof window !== 'undefined' && window.Plotly?.Plots?.resize) {
        try {
          window.Plotly.Plots.resize(plotEl);
        } catch (e) {
          // Plotly's resize can throw on unmounted/transitioning charts; ignore.
        }
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!spec || !Array.isArray(spec.data)) {
    return <ChartFallbackPanel title={title} error="Invalid chart spec" onRegenerate={onRegenerate} />;
  }

  // Resolve at click time so a chip whose storeId hasn't registered yet
  // (mid-stream) or a plexId-only source still lands on the Sources row.
  const handleSourceClick = (storeId, src) => {
    const id = storeId || (src?.id && resolveStoreId ? resolveStoreId(src.id, { synthesize: true }) : null);
    if (id && sourceReferencesStore?.scrollToSource) sourceReferencesStore.scrollToSource(id);
  };

  const orientation = spec.layout?.legend?.orientation === "h" ? "h" : "v";
  const traceChips = spec.data
    .map((trace, idx) => {
      const sid = columnSourceId(trace, String(idx));
      const src = sid ? bySpecId.get(sid) : null;
      return src ? { idx, src, storeId: storeIdFor(sid), label: trace?.name || `Trace ${idx + 1}` } : null;
    })
    .filter(Boolean);

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {traceChips.length > 0 && (
        <div
          className="plotly-prov-strip"
          data-testid="plotly-prov-strip"
          data-orientation={orientation}
          style={{ display: "flex", flexDirection: orientation === "h" ? "row" : "column",
                   gap: 6, padding: "4px 8px", flexWrap: "wrap" }}
        >
          {traceChips.map((t) => (
            <span key={t.idx} className="plotly-prov-strip__item" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span className="plotly-prov-strip__trace">{t.label}</span>
              <SourceColChip src={t.src} storeId={t.storeId} onClick={handleSourceClick} />
            </span>
          ))}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0 }}>
        <PlotlyErrorBoundary title={title} onRegenerate={onRegenerate} resetSpec={spec}>
          <Suspense fallback={<div className="plotly-loading">Loading chart…</div>}>
            <Plot
              data={spec.data}
              layout={buildPlotlyLayout(spec.layout, surfaceColor)}
              config={{ displaylogo: false, responsive: true }}
              style={{ width: "100%", height: "100%" }}
              useResizeHandler
            />
          </Suspense>
        </PlotlyErrorBoundary>
      </div>
    </div>
  );
};
